import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api";
import { useNotification } from "~hooks/useNotification";

export const useFetchPool = (poolCode) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const URL = `${sport}/o/${organizationId}/seasons/${seasonId}/pools/${poolCode}`;

  return useQuery({
    queryKey: ["pools", seasonId, poolCode],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data?.[0];
    },
    enabled: !!poolCode,
  });
};

export const useFetchPoolsPagination = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}/pools`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["pools", seasonId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useFetchPools = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}/pools`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["pools", seasonId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useCreatePool = () => {
  const { mutateData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/pools`,
        data,
      });
      return result?.data?.[0];
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "pool.add.success",
          defaultMessage: "Pool added successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["pools"] });
    },
  });
};

export const useUpdatePool = () => {
  const { mutateData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ data, poolCode }) => {
      const result = await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/pools/${poolCode}`,
        method: "PUT",
        data,
      });
      return result?.data?.[0];
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "pool.update.success",
          defaultMessage: "Pool updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["pools"] });
    },
  });
};

export const useDeletePool = () => {
  const { sport, organizationId, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (poolCode) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}/pools/${poolCode}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "pool.delete.success",
          defaultMessage: "Pool deleted successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["pools"] });
    },
  });
};
